import React, { useEffect, useState, useCallback, useRef } from 'react';
import useTheme from '../hooks/useTheme';
import './Modal.css';

const Settings = ({ onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const [gridSize, setGridSize] = useState(() => localStorage.getItem('gridSize') || '4');
  const [showOutOfStock, setShowOutOfStock] = useState(() => {
    const stored = localStorage.getItem('showOutOfStock');
    return stored === null ? true : stored === 'true';
  });
  const [showAllProducts, setShowAllProducts] = useState(() => {
    return localStorage.getItem('showAllProducts') === 'true';
  });
  const [concurrentRequests, setConcurrentRequests] = useState(() => localStorage.getItem('concurrentRequests') || '100');
  const [defaultSort, setDefaultSort] = useState(() => localStorage.getItem('defaultSort') || 'products');
  const [retryAttempts, setRetryAttempts] = useState(() => localStorage.getItem('retryAttempts') || '2');
  const [requestTimeout, setRequestTimeout] = useState(() => localStorage.getItem('requestTimeout') || '15');
  const [exportFormat, setExportFormat] = useState(() => localStorage.getItem('exportFormat') || 'csv');
  const [browserNotifications, setBrowserNotifications] = useState(() => {
    const stored = localStorage.getItem('browserNotifications');
    return stored === null ? true : stored === 'true';
  });
  const [soundNotifications, setSoundNotifications] = useState(() => {
    const stored = localStorage.getItem('soundNotifications');
    return stored === null ? true : stored === 'true';
  });
  const timeoutRef = useRef(null);

  // Request notification permission when browser notifications are enabled
  useEffect(() => {
    if (browserNotifications && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [browserNotifications]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Debounced save function
  const debouncedSave = useCallback((settings) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      Object.entries(settings).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      timeoutRef.current = null;
    }, 300);
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    const settings = {
      gridSize,
      showOutOfStock: showOutOfStock.toString(),
      showAllProducts: showAllProducts.toString(),
      concurrentRequests,
      defaultSort,
      retryAttempts,
      requestTimeout,
      exportFormat,
      browserNotifications: browserNotifications.toString(),
      soundNotifications: soundNotifications.toString()
    };

    debouncedSave(settings);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    gridSize, 
    showOutOfStock,
    showAllProducts,
    concurrentRequests, 
    defaultSort,
    retryAttempts,
    requestTimeout,
    exportFormat,
    browserNotifications,
    soundNotifications,
    debouncedSave
  ]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          <div className="simple-modal">
            <div className="settings-section">
              <h3 className="section-title">Display</h3>
              <div className="settings-group">
                <div className="setting-row">
                  <span>Theme</span>
                  <button 
                    className="theme-toggle-button" 
                    onClick={toggleTheme}
                    aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                  >
                    <i className={`fas fa-${theme === 'dark' ? 'sun' : 'moon'}`}></i>
                    {theme === 'dark' ? 'Light' : 'Dark'}
                  </button>
                </div>
                <div className="setting-row">
                  <span>Grid Size</span>
                  <div className="button-group">
                    {['3', '4', '5', '6'].map(size => (
                      <button
                        key={size}
                        className={`size-button ${gridSize === size ? 'active' : ''}`}
                        onClick={() => setGridSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="setting-row">
                  <span>Show Out of Stock</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={showOutOfStock}
                      onChange={(e) => setShowOutOfStock(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="setting-row">
                  <span>Show All Products</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={showAllProducts}
                      onChange={(e) => setShowAllProducts(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="setting-row">
                  <span>Default Sort Order</span>
                  <select 
                    className="setting-select"
                    value={defaultSort}
                    onChange={(e) => setDefaultSort(e.target.value)}
                  >
                    <option value="products">Most Variants</option>
                    <option value="priceDesc">Price: High to Low</option>
                    <option value="priceAsc">Price: Low to High</option>
                    <option value="store">Store Name</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3 className="section-title">Data</h3>
              <div className="settings-group">
                <div className="setting-row">
                  <span>Export Format</span>
                  <select 
                    className="setting-select"
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                  >
                    <option value="csv">CSV (Spreadsheet)</option>
                    <option value="json">JSON (Data)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3 className="section-title">Network</h3>
              <div className="settings-group">
                <div className="setting-row">
                  <span>Concurrent Requests</span>
                  <select 
                    className="setting-select"
                    value={concurrentRequests}
                    onChange={(e) => setConcurrentRequests(e.target.value)}
                  >
                    <option value="10">10 requests</option>
                    <option value="25">25 requests</option>
                    <option value="50">50 requests</option>
                    <option value="100">100 requests (recommended)</option>
                    <option value="150">150 requests</option>
                    <option value="200">200 requests</option>
                  </select>
                </div>
                <div className="setting-row">
                  <span>Retry Attempts</span>
                  <select 
                    className="setting-select"
                    value={retryAttempts}
                    onChange={(e) => setRetryAttempts(e.target.value)}
                  >
                    <option value="0">No retries</option>
                    <option value="1">1 retry</option>
                    <option value="2">2 retries (recommended)</option>
                    <option value="3">3 retries</option>
                  </select>
                </div>
                <div className="setting-row">
                  <span>Request Timeout</span>
                  <select 
                    className="setting-select"
                    value={requestTimeout}
                    onChange={(e) => setRequestTimeout(e.target.value)}
                  >
                    <option value="10">10 seconds</option>
                    <option value="15">15 seconds (recommended)</option>
                    <option value="20">20 seconds</option>
                    <option value="30">30 seconds</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3 className="section-title">Notifications</h3>
              <div className="settings-group">
                <div className="setting-row">
                  <span>Browser Notifications</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={browserNotifications}
                      onChange={(e) => setBrowserNotifications(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="setting-row">
                  <span>Sound Notifications</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={soundNotifications}
                      onChange={(e) => setSoundNotifications(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="version-info">
            Version 1.0.0
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 